<template>
  <Modal
    v-model:visible="isVisible"
    class="print-shipping-label-modal"
    :header="t('shipments.selectShippingLabel')"
    @keyup.esc="onCancel"
  >
    <InputWrapper
      v-if="hasTemplates || hasAttachedLabels"
      v-slot="{ ariaProps, inputId }"
      class="mb-3"
      input-id="print-shipping-label-template"
      :label="dropdownLabel"
    >
      <RygenDropdown
        :id="inputId"
        v-model="selected"
        v-bind="ariaProps"
        class="w-full"
        append-to="body"
        option-label="label"
        option-value="value"
        option-disabled="disabled"
        :options="dropdownOptions"
        :placeholder="dropdownLabel"
      >
        <template #option="{ option }">
          <span
            v-if="option.isHeader"
            class="font-semibold text-color-secondary"
          >
            {{ option.label }}
          </span>
          <span v-else :class="{ 'pl-3': hasAttachedLabels }">
            {{ option.label }}
          </span>
        </template>
      </RygenDropdown>
    </InputWrapper>

    <Message v-if="showWarning" severity="info" class="rygen-message mb-3">
      {{ t('shipments.shippingLabelTemplateRuleWarning') }}
    </Message>

    <div v-if="isZplEnabled" class="mb-3">
      <span id="print-shipping-label-format" class="block mb-1 font-semibold">
        {{ t('shipments.shippingLabelFormat') }}
      </span>
      <SelectButton
        v-model="format"
        :options="formatOptions"
        option-label="label"
        option-value="value"
        :allow-empty="false"
        aria-labelledby="print-shipping-label-format"
      />
    </div>

    <div v-if="showPageSizes">
      <span id="print-shipping-label-size" class="block mb-1 font-semibold">
        {{ t('shipments.shippingLabelSize') }}
      </span>
      <div
        class="grid -m-1"
        role="radiogroup"
        aria-labelledby="print-shipping-label-size"
      >
        <div
          v-for="option in sizeOptions"
          :key="option.value"
          class="col-6 p-1"
        >
          <button
            type="button"
            role="radio"
            class="label-option flex flex-column align-items-center gap-1 w-full p-2"
            :class="{ selected: size === option.value }"
            :aria-checked="size === option.value"
            :disabled="isSizeDisabled(option.value)"
            :data-test="`size-${option.value}`"
            @click="size = option.value"
          >
            <img :src="option.image" alt="" class="max-w-full" />
            <span>{{ option.label }}</span>
          </button>
        </div>
      </div>
      <small v-if="isZpl" class="block mt-2 text-center">
        {{ t('shipments.zplFourBySixOnly') }}
      </small>
    </div>

    <small v-if="selectedAttachedLabel && isZpl" class="block">
      {{ t('shipments.zplAttachedLabelHint') }}
    </small>

    <Message
      v-if="isTemplateZplIncompatible"
      severity="warn"
      class="rygen-message"
    >
      {{ t('shipments.zplTemplateNotFourBySix') }}
    </Message>

    <template #footer>
      <RygenButton
        :label="t('shipments.printShippingLabel')"
        :disabled="!canPrint"
        @click="onPrint"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { PropType, computed, ref, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import api from '@/api';
import avery5126Image from '@/assets/img/shippingLabel2_5126.webp';
import avery5168Image from '@/assets/img/shippingLabel4_5168.webp';
import avery5264Image from '@/assets/img/shippingLabel6_5264.webp';
import fourBySixImage from '@/assets/img/shippingLabelFourBySix.jpg';
import {
  DocumentTemplateMatch,
  DocumentType,
  Shipment,
  ShippingLabelFormat,
  SignedDocument,
} from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';
import { sortShippingLabelsNewestFirst } from '@/util/ShippingLabelUtil';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import Message from 'primevue/message';
import SelectButton from 'primevue/selectbutton';

const defaultValue = -1;
const FOUR_BY_SIX = 'FOUR_BY_SIX';
const ATTACHED_PREFIX = 'attached:';
// Attached labels the backend can convert to ZPL. Keep in sync with StoredShippingLabels.ZPL_CONVERTIBLE.
const ZPL_CONVERTIBLE_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/bmp',
  'image/tiff',
];

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (event: 'cancel'): void;
  (
    event: 'confirm',
    type: string,
    templateId: number | undefined,
    format: ShippingLabelFormat,
  ): void;
  (
    event: 'printAttached',
    document: SignedDocument,
    format: ShippingLabelFormat,
  ): void;
}>();

const props = defineProps({
  showWarning: {
    type: Boolean,
    default: false,
  },
  // Shipping label documents already attached to the shipment; offered ahead of the templates.
  attachedLabels: {
    type: Array as PropType<SignedDocument[]>,
    default: () => [],
  },
});
const { showWarning, attachedLabels } = toRefs(props);

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const isZplEnabled = useFlag('zpl-shipping-label-feat-flag');

const options = ref<DocumentTemplateMatch>();
const selected = ref<number | string>(defaultValue);
const format = ref(ShippingLabelFormat.PDF);
const size = ref(FOUR_BY_SIX);
const isLoading = ref(false);

const sizeLabelKeys: Record<string, string> = {
  [FOUR_BY_SIX]: 'shipments.shippingLabelFourBySix',
  AVERY_5126: 'shipments.shippingLabel2',
  AVERY_5168: 'shipments.shippingLabel4',
  AVERY_5264: 'shipments.shippingLabel6',
  LETTER: 'shipments.shippingLabelLetter',
};

const sizeLabel = (size?: string) => {
  const key = size ? sizeLabelKeys[size] : undefined;
  return key ? t(key) : undefined;
};

const sizeOptions = computed(() =>
  [
    { value: FOUR_BY_SIX, image: fourBySixImage },
    { value: 'AVERY_5126', image: avery5126Image },
    { value: 'AVERY_5168', image: avery5168Image },
    { value: 'AVERY_5264', image: avery5264Image },
  ].map((option) => ({ ...option, label: sizeLabel(option.value) })),
);

const hasTemplates = computed(() => !!options.value?.templates.length);

const sortedAttachedLabels = computed(() =>
  isZplEnabled.value ? sortShippingLabelsNewestFirst(attachedLabels.value) : [],
);

const attachedValue = (document: SignedDocument) =>
  `${ATTACHED_PREFIX}${document.id}`;

const hasAttachedLabels = computed(() => sortedAttachedLabels.value.length > 0);

// The list also holds attached documents, so "Template" no longer describes every choice.
const dropdownLabel = computed(() =>
  hasAttachedLabels.value
    ? t('shipments.shippingLabelSource')
    : t('templates.templates', 1),
);

const dropdownOptions = computed(() => {
  const defaultOption = { label: t('common.default'), value: defaultValue };

  const templateOptions = (options.value?.templates ?? []).map((template) => {
    const size = sizeLabel(template.documentSize);
    return {
      label: size ? `${template.name} (${size})` : template.name,
      value: template.id,
    };
  });

  if (!hasAttachedLabels.value) {
    return [defaultOption, ...templateOptions];
  }

  // Group headers are disabled rows: RygenDropdown can't use PrimeVue's option groups.
  return [
    {
      label: t('shipments.attachedShippingLabels'),
      value: 'header:attached',
      disabled: true,
      isHeader: true,
    },
    ...sortedAttachedLabels.value.map((document) => ({
      label: document.documentName,
      value: attachedValue(document),
    })),
    {
      label: t('shipments.generateFromTemplate'),
      value: 'header:templates',
      disabled: true,
      isHeader: true,
    },
    defaultOption,
    ...templateOptions,
  ];
});

const selectedAttachedLabel = computed(() =>
  sortedAttachedLabels.value.find(
    (document) => attachedValue(document) === selected.value,
  ),
);

const mediaType = (contentType?: string) =>
  contentType?.split(';')[0].trim().toLowerCase();

const canConvertToZpl = (document: SignedDocument) =>
  ZPL_CONVERTIBLE_CONTENT_TYPES.includes(mediaType(document.contentType) ?? '');

// An attached label prints as stored, so name its real file type rather than "PDF".
const attachedFileType = (document: SignedDocument) => {
  const extension = (document.fileName ?? document.documentName)?.match(
    /\.([a-z0-9]+)$/i,
  )?.[1];
  const subtype = mediaType(document.contentType)?.split('/')[1];
  const fileType =
    extension ?? (subtype && !subtype.includes('.') ? subtype : undefined);
  return fileType?.toUpperCase() ?? t('shipments.shippingLabelOriginalFormat');
};

const formatOptions = computed(() => {
  const label = selectedAttachedLabel.value;
  if (!label) {
    return [
      { label: 'PDF', value: ShippingLabelFormat.PDF },
      { label: 'ZPL', value: ShippingLabelFormat.ZPL },
    ];
  }
  // The non-ZPL choice opens the stored file as is.
  const original = {
    label: attachedFileType(label),
    value: ShippingLabelFormat.PDF,
  };
  return canConvertToZpl(label)
    ? [original, { label: 'ZPL', value: ShippingLabelFormat.ZPL }]
    : [original];
});

const showPageSizes = computed(
  () =>
    !selectedAttachedLabel.value &&
    (!hasTemplates.value || selected.value === defaultValue),
);

const selectedTemplate = computed(() =>
  options.value?.templates.find((option) => option.id === selected.value),
);

const isZpl = computed(
  () => isZplEnabled.value && format.value === ShippingLabelFormat.ZPL,
);

const isSizeDisabled = (value: string) => isZpl.value && value !== FOUR_BY_SIX;

const isTemplateZplIncompatible = computed(
  () =>
    !showPageSizes.value &&
    !selectedAttachedLabel.value &&
    isZpl.value &&
    selectedTemplate.value?.documentSize !== FOUR_BY_SIX,
);

const canPrint = computed(() => {
  if (isLoading.value) {
    return false;
  }
  if (showPageSizes.value || selectedAttachedLabel.value) {
    return true;
  }
  return (
    !!selectedTemplate.value?.documentSize && !isTemplateZplIncompatible.value
  );
});

watch(formatOptions, (available) => {
  if (!available.some((option) => option.value === format.value)) {
    format.value = ShippingLabelFormat.PDF;
  }
});

watch(format, (value) => {
  if (value === ShippingLabelFormat.ZPL) {
    size.value = FOUR_BY_SIX;
  }
});

const loadTemplates = async () => {
  // An attached label is what this button printed before the modal, so it wins over a matched template.
  // Select it up front so the choice is settled before templates finish loading.
  const newestAttachedLabel = sortedAttachedLabels.value[0];
  const initialSelection = newestAttachedLabel
    ? attachedValue(newestAttachedLabel)
    : defaultValue;
  selected.value = initialSelection;
  options.value = undefined;
  format.value = ShippingLabelFormat.PDF;
  size.value = FOUR_BY_SIX;

  const entity = parentEntity.value;
  if (!(entity instanceof Shipment) || entity.id == null) {
    return;
  }

  isLoading.value = true;
  try {
    options.value = await api.documentTemplate.matchDocumentTemplate(
      DocumentType.SHIPPING_LABEL,
      entity.id,
    );
    // Don't override an attached label or anything the user picked while templates loaded.
    if (!newestAttachedLabel && selected.value === initialSelection) {
      selected.value = options.value.matchedTemplateId ?? defaultValue;
    }
  } catch (e) {
    console.error('load shipping label templates error', entity.id, e);
    options.value = undefined;
  } finally {
    isLoading.value = false;
  }
};

const onCancel = async () => {
  emit('cancel');
  isVisible.value = false;
};

const onPrint = () => {
  const outputFormat = isZpl.value
    ? ShippingLabelFormat.ZPL
    : ShippingLabelFormat.PDF;

  if (selectedAttachedLabel.value) {
    emit('printAttached', selectedAttachedLabel.value, outputFormat);
  } else if (showPageSizes.value) {
    emit('confirm', size.value, undefined, outputFormat);
  } else if (selectedTemplate.value?.documentSize) {
    emit(
      'confirm',
      selectedTemplate.value.documentSize,
      selectedTemplate.value.id,
      outputFormat,
    );
  }
  isVisible.value = false;
};

watch(isVisible, (visible) => {
  if (visible) {
    loadTemplates();
  }
});
</script>

<style>
.print-shipping-label-modal {
  .label-option {
    border: 2px solid transparent;
    border-radius: var(--border-radius, 6px);
    background: none;
    cursor: pointer;

    &:hover:not(:disabled) {
      background-color: var(--p-highlight-background);
    }

    &.selected {
      border-color: var(--p-primary-color);
      background-color: var(--p-highlight-background);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
</style>
